import React, { useEffect, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  openCreateCourseDialog,
  getAllCourses
} from "../Redux/Dashboard/DashboardSlice";
import { Notify } from "../CommonPopup/Popup";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery, TextField, IconButton, Radio, RadioGroup, FormControlLabel, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { IoCloseCircleOutline, IoTrashOutline } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFilm, faImage, faPen, faFolderOpen, faEdit } from "@fortawesome/free-solid-svg-icons";
import "../Style/CreateCourseDialog.css";
import Spinner from '../Spinner/Spinner'
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import { updateCourse } from "../Redux/Auth/AuthSlice";

const CreateCourseDialog = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, reset, setValue, formState: { errors }, getValues } = useForm({
    defaultValues: {
      title: "",
      description: "",
      price: 0,
    },
  });

  const {
    loader,
    showCreateCourseDialog,
    selectedCourseFolder,
    allCoursesLessons = [],
  } = useSelector((s) => s.DashboardSlice);
  const courseId = selectedCourseFolder?.course?.course?._id;

  const [lessons, setLessons] = useState([]);
  const [deletedContent, setDeletedContent] = useState([]);
  const [selectedLessonIdx, setSelectedLessonIdx] = useState(null);
  const [selectedSubjectIdx, setSelectedSubjectIdx] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showVideoPrompt, setShowVideoPrompt] = useState(false);
  const [isYouTube, setIsYouTube] = useState(false);
  const [youTubeUrl, setYouTubeUrl] = useState("");
  const [youTubeError, setYouTubeError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [courseData, setCourseData] = useState({
    title: "",
    description: "",
    thumbnail: null,
    is_paid: false,
    price: 0,
  });
  const [activeButtonId, setActiveButtonId] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testGroups, setTestGroups] = useState([]);
  const [selectedTestGroupIdx, setSelectedTestGroupIdx] = useState(null);
  const [editingTestGroupIdx, setEditingTestGroupIdx] = useState(null);
  const [editingQuestionIdx, setEditingQuestionIdx] = useState(null);
  const fileInputRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Form for adding/editing test groups
  const {
    register: registerTestGroup,
    handleSubmit: handleTestGroupSubmit,
    reset: resetTestGroup,
    formState: { errors: testGroupErrors },
    setValue: setTestGroupValue
  } = useForm();

  // Form for adding/editing questions
  const {
    register: registerQuestion,
    handleSubmit: handleQuestionSubmit,
    reset: resetQuestion,
    formState: { errors: questionErrors },
    watch: watchQuestion,
    setValue: setQuestionValue
  } = useForm({
    defaultValues: {
      question: "",
      options: ["", "", "", "", ""],
      answer: ""
    }
  });

  useEffect(() => {
    if (selectedCourseFolder?.course?.course) {
      const course = selectedCourseFolder.course.course;
      setCourseData({
        title: course.title || "",
        description: course.description || "",
        thumbnail: course.thumbnail || null,
        is_paid: course.is_paid || false,
        price: course.is_paid ? course.price || 0 : 0,
      });
      setValue("title", course.title || "");
      setValue("description", course.description || "");
      setValue("price", course.is_paid ? course.price || 0 : 0);
      console.log("Loaded course data:", { is_paid: course.is_paid, price: course.price });
      if (course.lessons) {
        const mappedLessons = course.lessons.map(lesson => ({
          _id: lesson._id,
          lesson_name: lesson.lesson_name,
          description: lesson.description || "",
          thumbnail: lesson.thumbnail || null,
          subjects: lesson.subjects?.map(subject => ({
            _id: subject._id,
            chapter_name: subject.chapter_name,
            description: subject.description || "",
            thumbnail: subject.thumbnail || null,
            content: subject.content || { pdf: [], video: [], image: [], test: [] }
          })) || []
        }));
        setLessons(structuredClone(mappedLessons));
        if (mappedLessons.length > 0) {
          setSelectedLessonIdx(0);
          setCurrentStep(1);
        }
      }
    }
    dispatch(getAllCourses({ isPaid: "" }));
  }, [selectedCourseFolder, dispatch, setValue]);

  const getYouTubeVideoId = (url) => {
    try {
      const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
      const match = url.match(regex);
      if (!match || !match[1]) {
        throw new Error("Invalid YouTube URL format");
      }
      return match[1];
    } catch (error) {
      console.error("Error parsing YouTube URL:", error);
      return null;
    }
  };

  const fetchYouTubeVideoTitle = async (url) => {
    try {
      const response = await fetch(`https://noembed.com/embed?format=json&url=${encodeURIComponent(url)}`);
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      return data.title || null;
    } catch (error) {
      console.error('Error fetching YouTube video title:', error);
      return null;
    }
  };

  const handleClose = () => {
    reset({ title: "", description: "", price: 0 });
    setLessons([]);
    setDeletedContent([]);
    setSelectedLessonIdx(null);
    setSelectedSubjectIdx(null);
    setCurrentStep(0);
    setShowSidebar(true);
    setShowVideoPrompt(false);
    setIsYouTube(false);
    setYouTubeUrl("");
    setYouTubeError("");
    setUploading(false);
    setShowTestModal(false);
    setTestGroups([]);
    setSelectedTestGroupIdx(null);
    setEditingTestGroupIdx(null);
    setEditingQuestionIdx(null);
    setCourseData({ title: "", description: "", thumbnail: null, is_paid: false, price: 0 });
    setActiveButtonId(null);
    dispatch(openCreateCourseDialog(false));
  };

  const addLesson = () => {
    const values = getValues();
    const hasErrors = Object.keys(errors).length > 0;
    const isTitleValid = values.title && values.title.trim() !== "";
    const isDescriptionValid = values.description && values.description.trim() !== "";
    const isThumbnailValid = courseData.thumbnail !== null && (courseData.thumbnail instanceof File || typeof courseData.thumbnail === "string");
    const isPriceValid = !courseData.is_paid || (values.price >= 0);

    if (!isTitleValid || !isDescriptionValid || !isThumbnailValid || !isPriceValid || hasErrors) {
      const errorMessages = [];
      if (!isTitleValid || errors.title) errorMessages.push("Title is required");
      if (!isDescriptionValid || errors.description) errorMessages.push("Description is required");
      if (!isThumbnailValid) errorMessages.push("Thumbnail is required");
      if (!isPriceValid || errors.price) errorMessages.push("Valid price is required for paid courses");

      Notify({
        data: "error",
        msg: `Please fill all course details: ${errorMessages.join(", ")}.`,
        type: "error",
      });
      console.log("Lesson add failed:", {
        title: isTitleValid ? "Valid" : "Invalid",
        description: isDescriptionValid ? "Valid" : "Invalid",
        thumbnail: isThumbnailValid ? "Valid" : "Invalid",
        price: isPriceValid ? values.price : "Invalid",
      });
      return;
    }

    const newLesson = {
      lesson_name: `Lesson ${lessons.length + 1}`,
      description: "",
      thumbnail: null,
      subjects: [],
    };
    setLessons([...lessons, structuredClone(newLesson)]);
    setSelectedLessonIdx(lessons.length);
    setSelectedSubjectIdx(null);
    setCurrentStep(1);
    Notify({ data: "success", msg: "Lesson added successfully", type: "success" });
    console.log("Added lesson:", newLesson);
  };

  const addSubject = (lessonIdx, navigate = true) => {
    if (lessonIdx === null || lessonIdx < 0 || lessonIdx >= lessons.length) {
      console.error("Invalid lesson index:", lessonIdx);
      return;
    }
    const updatedLessons = structuredClone(lessons);
    updatedLessons[lessonIdx].subjects.push({
      chapter_name: `Subject ${updatedLessons[lessonIdx].subjects.length + 1}`,
      description: "",
      thumbnail: null,
      content: { pdf: [], video: [], image: [], test: [] },
    });
    setLessons(updatedLessons);
    if (navigate) {
      setSelectedLessonIdx(lessonIdx);
      setSelectedSubjectIdx(updatedLessons[lessonIdx].subjects.length - 1);
      setCurrentStep(2);
    }
    console.log("Added subject to lesson:", lessonIdx, updatedLessons[lessonIdx].subjects);
  };

  const deleteLesson = (index, e) => {
    e.stopPropagation();
    console.log("Deleting lesson:", { index, lessonsBefore: lessons });
    const updatedLessons = lessons.filter((_, i) => i !== index);
    setLessons(updatedLessons);
    if (selectedLessonIdx === index) {
      setSelectedLessonIdx(null);
      setSelectedSubjectIdx(null);
      setCurrentStep(0);
    }
    Notify({ data: "success", msg: `Lesson ${index + 1} deleted successfully`, type: "success" });
    console.log("Lesson deleted:", { index, lessonsAfter: updatedLessons });
  };

  const deleteSubject = (lessonIdx, subjectIdx, e) => {
    e.stopPropagation();
    console.log("Deleting subject:", { lessonIdx, subjectIdx, lessonsBefore: lessons });
    if (!lessons[lessonIdx]) {
      console.error("Invalid lesson index:", lessonIdx);
      Notify({ data: "error", msg: "Cannot delete subject: Invalid lesson", type: "error" });
      return;
    }
    const updatedLessons = structuredClone(lessons);
    updatedLessons[lessonIdx].subjects = updatedLessons[lessonIdx].subjects.filter((_, i) => i !== subjectIdx);
    setLessons(updatedLessons);
    if (selectedLessonIdx === lessonIdx && selectedSubjectIdx === subjectIdx) {
      setSelectedSubjectIdx(null);
      setCurrentStep(1);
    }
    Notify({ data: "success", msg: `Subject ${subjectIdx + 1} deleted successfully`, type: "success" });
    console.log("Subject deleted:", { lessonIdx, subjectIdx, lessonsAfter: updatedLessons });
  };

  const deleteContent = (type, index) => {
    if (selectedLessonIdx === null || selectedSubjectIdx === null) {
      console.error("Invalid selection:", { selectedLessonIdx, selectedSubjectIdx });
      return;
    }
    console.log("Deleting content:", { type, index });
    const updatedLessons = structuredClone(lessons);
    const contentItem = updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type][index];
    if (contentItem._id) {
      setDeletedContent(prev => [...prev, { _id: contentItem._id, type }]);
    }
    updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type] =
      updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].filter((_, i) => i !== index);
    setLessons(updatedLessons);
    Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} removed successfully`, type: "success" });
    console.log("Content deleted:", { type, index, deletedContentId: contentItem._id, lessonsAfter: updatedLessons });
  };

  const validateImageFile = (file) => {
    if (!file) return false;
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    return validTypes.includes(file.type);
  };

  const handleCourseChange = (field, value) => {
    if (field === "thumbnail") {
      const file = value.target.files[0];
      if (file && !validateImageFile(file)) {
        Notify({
          data: "error",
          msg: "Invalid thumbnail format. Please upload a .jpg, .jpeg, or .png image.",
          type: "error",
        });
        return;
      }
    }
    setCourseData((prev) => {
      const newData = {
        ...prev,
        [field]: field === "thumbnail" ? value.target.files[0] : field === "is_paid" ? value : field === "price" ? parseFloat(value) || 0 : value,
      };
      if (field === "price") {
        setValue("price", parseFloat(value) || 0);
      } else if (field === "title") {
        setValue("title", value);
      } else if (field === "description") {
        setValue("description", value);
      } else if (field === "is_paid") {
        setValue("price", value ? prev.price : 0);
      }
      console.log("Course data changed:", { field, value: field === "thumbnail" ? value.target.files[0]?.name : value });
      return newData;
    });
  };

  const handleLessonChange = (index, field, value) => {
    if (field === "thumbnail") {
      const file = value.target.files[0];
      if (file && !validateImageFile(file)) {
        Notify({
          data: "error",
          msg: "Invalid thumbnail format. Please upload a .jpg, .jpeg, or .png image.",
          type: "error",
        });
        return;
      }
    }
    const updatedLessons = structuredClone(lessons);
    updatedLessons[index][field] = field === "thumbnail" ? value.target.files[0] : value;
    setLessons(updatedLessons);
    console.log("Lesson changed:", { index, field, value: field === "thumbnail" ? value.target.files[0]?.name : value });
  };

  const handleSubjectChange = (lessonIndex, subjectIndex, field, value) => {
    if (field === "thumbnail") {
      const file = value.target.files[0];
      if (file && !validateImageFile(file)) {
        Notify({
          data: "error",
          msg: "Invalid thumbnail format. Please upload a .jpg, .jpeg, or .png image.",
          type: "error",
        });
        return;
      }
    }
    const updatedLessons = structuredClone(lessons);
    updatedLessons[lessonIndex].subjects[subjectIndex][field] =
      field === "thumbnail" ? value.target.files[0] : value;
    setLessons(updatedLessons);
    console.log("Subject changed:", { lessonIndex, subjectIndex, field, value: field === "thumbnail" ? value.target.files[0]?.name : value });
  };

  const addVideoContent = async () => {
    if (selectedLessonIdx === null || selectedSubjectIdx === null) {
      console.error("Invalid selection for adding video:", { selectedLessonIdx, selectedSubjectIdx });
      Notify({ data: "error", msg: "Please select a subject to add video content", type: "error" });
      return;
    }

    if (isYouTube) {
      if (!youTubeUrl) {
        setYouTubeError("Please enter a YouTube URL.");
        return;
      }
      const videoId = getYouTubeVideoId(youTubeUrl);
      if (!videoId) {
        setYouTubeError("Invalid YouTube URL.");
        return;
      }
      const videoTitle = await fetchYouTubeVideoTitle(youTubeUrl);
      if (!videoTitle) {
        setYouTubeError("Failed to fetch video title. Please try again.");
        return;
      }
      const updatedLessons = structuredClone(lessons);
      const videoContent = updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content.video;
      videoContent.push({
        name: videoTitle,
        url: youTubeUrl,
        type: "youtube",
      });
      setLessons(updatedLessons);
      setYouTubeUrl("");
      setYouTubeError("");
      setShowVideoPrompt(false);
      Notify({ data: "success", msg: "YouTube video added successfully", type: "success" });
      console.log("Added YouTube video:", { title: videoTitle, url: youTubeUrl, lessonsAfter: updatedLessons });
    } else {
      if (!fileInputRef.current) {
        console.error("File input ref is not available");
        Notify({ data: "error", msg: "Failed to open file picker", type: "error" });
        return;
      }
      setUploading(true);
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    console.log("Handling file input change");
    const files = Array.from(e.target.files);
    console.log("Selected files:", files.map(f => f.name));
    if (files.length === 0) {
      setUploading(false);
      Notify({ data: "warning", msg: "No video files selected", type: "warning" });
      return;
    }

    const validFiles = files.filter((file) => {
      const isValidType = ["video/mp4", "video/quicktime"].includes(file.type);
      if (!isValidType) {
        Notify({ data: "error", msg: `Invalid file type: ${file.name}. Please select .mp4 or .mov files.`, type: "error" });
      }
      return isValidType;
    });

    if (validFiles.length === 0) {
      setUploading(false);
      Notify({ data: "error", msg: "No valid video files selected", type: "error" });
      return;
    }

    const updatedLessons = structuredClone(lessons);
    const newFiles = validFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      fileData: file,
      type: "file",
    }));
    updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content.video.push(...newFiles);
    setLessons(updatedLessons);
    setShowVideoPrompt(false);
    setUploading(false);
    Notify({ data: "success", msg: `Added ${validFiles.length} video file(s) successfully`, type: "success" });
    console.log("Added video files:", { files: newFiles.map(f => f.name), lessonsAfter: updatedLessons });
  };

  const addContent = (type, isEdit = false, testIndex = null) => {
    if (selectedLessonIdx === null || selectedSubjectIdx === null) {
      console.error("Invalid selection for adding content:", { selectedLessonIdx, selectedSubjectIdx });
      Notify({ data: "error", msg: "Please select a subject to add content", type: "error" });
      return;
    }

    if (type === "video") {
      setShowVideoPrompt(true);
      setIsYouTube(false);
      setYouTubeUrl("");
      setYouTubeError("");
      setShowTestModal(false);
      return;
    }

    if (type === "test") {
      setShowTestModal(true);
      const existingTestGroups = [...lessons[selectedLessonIdx].subjects[selectedSubjectIdx].content.test];
      setTestGroups(existingTestGroups);
      if (isEdit && testIndex !== null) {
        setSelectedTestGroupIdx(testIndex);
      } else {
        setSelectedTestGroupIdx(existingTestGroups.length > 0 ? 0 : null);
      }
      setEditingTestGroupIdx(null);
      setEditingQuestionIdx(null);
      setShowVideoPrompt(false);
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = type === "pdf" ? ".pdf" : type === "image" ? ".jpg,.jpeg,.png" : ".pdf";
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      console.log(`Selected ${type} files:`, files.map(f => f.name));
      if (files.length === 0) {
        Notify({ data: "warning", msg: `No ${type} files selected`, type: "warning" });
        return;
      }

      const validFiles = files.filter((file) => {
        const isValidType = type === "pdf" ? file.type === "application/pdf" : ["image/jpeg", "image/png"].includes(file.type);
        if (!isValidType) {
          Notify({ data: "error", msg: `Invalid file type: ${file.name}`, type: "error" });
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        Notify({ data: "error", msg: `No valid ${type} files selected`, type: "error" });
        return;
      }

      const updatedLessons = structuredClone(lessons);
      const newFiles = validFiles.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        fileData: file,
        type: "file",
      }));
      updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].push(...newFiles);
      setLessons(updatedLessons);
      Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} file(s) added successfully`, type: "success" });
      console.log("Added content:", { type, files: newFiles.map(f => f.name), lessonsAfter: updatedLessons });
    };
    input.click();
  };

  const updateTest = (testIndex) => {
    addContent("test", true, testIndex);
  };

  const deleteTest = (index) => {
    deleteContent("test", index);
  };

  const addTestGroup = (data) => {
    if (editingTestGroupIdx !== null) {
      setTestGroups((prevGroups) => {
        const updatedGroups = [...prevGroups];
        updatedGroups[editingTestGroupIdx] = {
          ...updatedGroups[editingTestGroupIdx],
          name: data.testGroupName
        };
        return updatedGroups;
      });
      setEditingTestGroupIdx(null);
      resetTestGroup();
      Notify({ data: "success", msg: "Test group updated successfully", type: "success" });
    } else {
      const newGroup = {
        name: data.testGroupName,
        tests: []
      };
      setTestGroups([...testGroups, newGroup]);
      setSelectedTestGroupIdx(testGroups.length);
      resetTestGroup();
      Notify({ data: "success", msg: "Test group added successfully", type: "success" });
    }
  };

  const editTestGroup = (groupIdx) => {
    setEditingTestGroupIdx(groupIdx);
    setTestGroupValue("testGroupName", testGroups[groupIdx].name);
  };

  const deleteTestGroup = (groupIdx) => {
    const updated = testGroups.filter((_, idx) => idx !== groupIdx);
    setTestGroups(updated);
    if (selectedTestGroupIdx === groupIdx) {
      setSelectedTestGroupIdx(updated.length > 0 ? 0 : null);
    }
    if (editingTestGroupIdx === groupIdx) {
      setEditingTestGroupIdx(null);
      resetTestGroup();
    }
    Notify({ data: "success", msg: "Test group deleted successfully", type: "success" });
  };

  const addQuestion = (data) => {
    if (isSubmittingRef.current) {
      console.log('Submission blocked: already in progress');
      return;
    }
    isSubmittingRef.current = true;

    const submissionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    console.log('Submission ID:', submissionId);

    let hasUpdatedState = false;

    if (!data.question || !data.options.every((opt) => opt) || !data.answer) {
      Notify({
        data: "error",
        msg: "Please fill all question details correctly",
        type: "error"
      });
      isSubmittingRef.current = false;
      return;
    }

    if (selectedTestGroupIdx === null || selectedTestGroupIdx >= testGroups.length) {
      Notify({
        data: "error",
        msg: "No test group selected",
        type: "error"
      });
      isSubmittingRef.current = false;
      return;
    }

    const newQuestion = {
      question: data.question,
      options: [...data.options],
      answer: data.answer
    };

    setTestGroups((prevGroups) => {
      if (hasUpdatedState) {
        console.log('setTestGroups skipped: state already updated for submission', submissionId);
        return prevGroups;
      }

      console.log('setTestGroups called for submission', submissionId);
      hasUpdatedState = true;

      const updatedGroups = [...prevGroups];
      if (editingQuestionIdx !== null) {
        updatedGroups[selectedTestGroupIdx].tests[editingQuestionIdx] = newQuestion;
        if (updatedGroups[selectedTestGroupIdx].tests[editingQuestionIdx]._id) {
          newQuestion._id = updatedGroups[selectedTestGroupIdx].tests[editingQuestionIdx]._id;
        }
        Notify({ data: "success", msg: "Question updated successfully", type: "success" });
      } else {
        updatedGroups[selectedTestGroupIdx].tests = [...updatedGroups[selectedTestGroupIdx].tests, newQuestion];
        Notify({ data: "success", msg: "Question added successfully", type: "success" });
      }
      return updatedGroups;
    });

    setEditingQuestionIdx(null);
    resetQuestion({
      question: "",
      options: ["", "", "", "", ""],
      answer: ""
    });

    isSubmittingRef.current = false;
    console.log('Submission completed for ID:', submissionId);
  };

  const editQuestion = (questionIdx) => {
    const question = testGroups[selectedTestGroupIdx].tests[questionIdx];
    setEditingQuestionIdx(questionIdx);
    setQuestionValue("question", question.question, { shouldValidate: true, shouldDirty: true });
    question.options.forEach((opt, i) =>
      setQuestionValue(`options.${i}`, opt, { shouldValidate: true, shouldDirty: true })
    );
    setQuestionValue("answer", question.answer, { shouldValidate: false, shouldDirty: true });
  };

  const deleteQuestion = (questionIdx) => {
    const updatedGroups = [...testGroups];
    const question = updatedGroups[selectedTestGroupIdx].tests[questionIdx];
    if (question._id) {
      setDeletedContent(prev => [...prev, { _id: question._id, type: "test_question" }]);
    }
    updatedGroups[selectedTestGroupIdx].tests = updatedGroups[selectedTestGroupIdx].tests.filter(
      (_, idx) => idx !== questionIdx
    );
    setTestGroups(updatedGroups);
    if (editingQuestionIdx === questionIdx) {
      setEditingQuestionIdx(null);
      resetQuestion({
        question: "",
        options: ["", "", "", "", ""],
        answer: ""
      });
    }
    Notify({ data: "success", msg: "Question deleted successfully", type: "success" });
  };

  const saveTests = () => {
    if (testGroups.length === 0) {
      Notify({ data: "error", msg: "No test groups created", type: "error" });
      return;
    }
    const updatedLessons = structuredClone(lessons);
    updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content.test = [...testGroups];
    setLessons(updatedLessons);
    setShowTestModal(false);
    setTestGroups([]);
    setSelectedTestGroupIdx(null);
    setEditingTestGroupIdx(null);
    setEditingQuestionIdx(null);
    Notify({ data: "success", msg: "Tests saved successfully", type: "success" });
    console.log("Saved tests:", { testGroups, lessonsAfter: updatedLessons });
  };

  const handleSelectSubject = (lessonIdx, subjectIdx) => {
    setSelectedLessonIdx(lessonIdx);
    setSelectedSubjectIdx(subjectIdx);
    setCurrentStep(2);
    console.log("Selected subject:", { lessonIdx, subjectIdx });
  };

  const handleSelectContent = (lessonIdx, subjectIdx, e) => {
    e.stopPropagation();
    setSelectedLessonIdx(lessonIdx);
    setSelectedSubjectIdx(subjectIdx);
    setCurrentStep(3);
    console.log("Navigated to content for:", { lessonIdx, subjectIdx });
  };

  const handleAdd = (menuId, e) => {
    e.stopPropagation();
    setActiveButtonId(menuId);
    setTimeout(() => setActiveButtonId(null), 500);
    console.log("Handling add for:", menuId);
    if (menuId.startsWith("lesson-")) {
      const lessonIdx = parseInt(menuId.split("-")[1], 10);
      addSubject(lessonIdx, true);
    }
  };

  const handleSubmitLessons = async (data) => {
    if (!courseId) {
      Notify({ data: "error", msg: "Course ID missing!", type: "error" });
      return;
    }

    const formData = new FormData();

    formData.append("title", courseData.title);
    formData.append("description", courseData.description);
    formData.append("is_paid", courseData.is_paid);
    formData.append("price", courseData.is_paid ? getValues("price") : 0);

    if (courseData.thumbnail instanceof File) {
      formData.append("files", courseData.thumbnail, courseData.thumbnail.name);
      formData.append("thumbnail", courseData.thumbnail.name);
    } else if (typeof courseData.thumbnail === "string") {
      formData.append("thumbnail", courseData.thumbnail);
    }

    const structuredLessons = lessons.map((lesson) => {
      if (lesson.thumbnail instanceof File) {
        formData.append("files", lesson.thumbnail, lesson.thumbnail.name);
      }

      return {
        _id: lesson._id || undefined,
        lesson_name: lesson.lesson_name,
        description: lesson.description,
        thumbnail: lesson.thumbnail instanceof File ? lesson.thumbnail.name : lesson.thumbnail || "",
        subjects: lesson.subjects.map((subject) => {
          if (subject.thumbnail instanceof File) {
            formData.append("files", subject.thumbnail, subject.thumbnail.name);
          }

          const content = {};
          ["pdf", "video", "image", "test"].forEach((type) => {
            content[type] = subject.content?.[type]?.map((item) => {
              if (type === "test") {
                return {
                  _id: item._id || undefined,
                  name: item.name,
                  tests: item.tests.map(q => ({
                    _id: q._id || undefined,
                    question: q.question,
                    options: [...q.options],
                    answer: q.answer
                  }))
                };
              }
              if (item.type === "youtube") {
                return { name: item.name, url: item.url, type: "youtube" };
              }
              if (item.fileData instanceof File) {
                formData.append("files", item.fileData, item.fileData.name);
                return { name: item.fileData.name, url: item.url, type: "s3" };
              }
              return item;
            }) || [];
          });

          return {
            _id: subject._id || undefined,
            chapter_name: subject.chapter_name,
            description: subject.description,
            thumbnail: subject.thumbnail instanceof File ? subject.thumbnail.name : subject.thumbnail || "",
            content,
          };
        }),
      };
    });

    formData.append("lessons", JSON.stringify(structuredLessons));
    if (deletedContent.length > 0) {
      formData.append("deletedContent", JSON.stringify(deletedContent));
    }
    console.log("Submitting FormData:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? value.name : value);
    }
    console.log("Submitting deletedContent:", deletedContent);
    console.log("Submitting is_paid:", courseData.is_paid);
    console.log("Submitting price:", courseData.is_paid ? getValues("price") : 0);

    try {
      const result = await dispatch(updateCourse({ courseId, FormData: formData })).unwrap();
      console.log("Course update success:", result);
      Notify({ data: "success", msg: "Course updated successfully", type: "success" });
      handleClose();
      dispatch(getAllCourses({ isPaid: "" }));
      navigate("/courses");
    } catch (error) {
      console.error("Course update failed:", error);
      Notify({ data: "error", msg: "Failed to update course", type: "error" });
    }
  };

  const stepLabels = ["Course", "Lesson", "Subject", "Content"];

  return (
    <Dialog
      fullScreen={fullScreen}
      open={showCreateCourseDialog}
      // onClose={handleClose}
      maxWidth="lg"
      PaperProps={{ className: "create-course-dialog" }}
    >
      {loader ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "300px",
          }}
        >
          <Spinner />
        </div>
      ) : (
        <>
          <div className="dialog-content">
            {/* Sidebar */}
            {showSidebar && (
              <div className="sidebar">
                <div className="sidebar-header">
                  <h3>Course Structure</h3>
                  <IconButton onClick={() => setShowSidebar(false)} aria-label="Close sidebar">
                    <IoCloseCircleOutline size={28} style={{ cursor: "pointer", color: "#f44336" }} />
                  </IconButton>
                </div>
                <div
                  className={`structure-title ${currentStep === 0 ? "active" : ""}`}
                  onClick={() => {
                    setSelectedLessonIdx(null);
                    setSelectedSubjectIdx(null);
                    setCurrentStep(0);
                  }}
                >
                  <span>Course Details</span>
                </div>
                {lessons.length === 0 ? (
                  <p className="no-data">No lessons added yet.</p>
                ) : (
                  <ul className="structure-list">
                    {lessons.map((lesson, lIdx) => (
                      <li key={lIdx} className="structure-item">
                        <div
                          className={`structure-title ${selectedLessonIdx === lIdx && currentStep === 1 ? "active" : ""}`}
                          onClick={() => {
                            setSelectedLessonIdx(lIdx);
                            setSelectedSubjectIdx(null);
                            setCurrentStep(1);
                          }}
                        >
                          <span>{lesson.lesson_name}</span>
                          <div className="structure-actions">
                            <IconButton
                              onClick={(e) => handleAdd(`lesson-${lIdx}`, e)}
                              className={`add-btnUpdate fw-bold ${activeButtonId === `lesson-${lIdx}` ? "active" : ""}`}
                              aria-label="Add subject"
                            >
                              +
                            </IconButton>
                            <IconButton
                              onClick={(e) => deleteLesson(lIdx, e)}
                              aria-label={`Delete lesson ${lesson.lesson_name}`}
                            >
                              <MdDelete size={16} color="#dc3545" />
                            </IconButton>
                          </div>
                        </div>
                        {lesson.subjects.length > 0 && (
                          <ul className="sub-list">
                            {lesson.subjects.map((subject, sIdx) => (
                              <li key={sIdx} className="structure-item">
                                <div
                                  className={`structure-title ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 2 ? "active" : ""}`}
                                  onClick={() => handleSelectSubject(lIdx, sIdx)}
                                >
                                  <span>{subject.chapter_name}</span>
                                  <div className="structure-actions">
                                    <IconButton
                                      onClick={(e) => handleSelectContent(lIdx, sIdx, e)}
                                      className={`content-nav-btn ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 3 ? "highlighted" : ""}`}
                                      aria-label={`View content for ${subject.chapter_name}`}
                                      title="View content"
                                    >
                                      <FontAwesomeIcon icon={faFolderOpen} size="1x" />
                                    </IconButton>
                                    <IconButton
                                      onClick={(e) => deleteSubject(lIdx, sIdx, e)}
                                      aria-label={`Delete subject ${subject.chapter_name}`}
                                    >
                                      <MdDelete size={16} color="#dc3545" />
                                    </IconButton>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setActiveButtonId("add-lesson");
                    setTimeout(() => setActiveButtonId(null), 500);
                    addLesson();
                  }}
                  className={`add-lesson-btn ${activeButtonId === "add-lesson" ? "active" : ""}`}
                  aria-label="Add new lesson"
                >
                  + Add Lesson
                </Button>
              </div>
            )}

            {/* Main Content */}
            <div className="main-content">
              <div className="header">
                <h2>Edit Course: {courseData.title}</h2>
                <div className="header-actions">
                  {!showSidebar && (
                    <Button variant="link" onClick={() => setShowSidebar(true)} className="show-structure-btn">
                      Show Course Structure
                    </Button>
                  )}
                  <IconButton onClick={handleClose} aria-label="Close dialog">
                    <IoCloseCircleOutline size={32} color="#dc3545" />
                  </IconButton>
                </div>
              </div>

              {/* Step Indicator */}
              <div className="step-indicator">
                {stepLabels.map((label, idx) => (
                  <div key={idx} className={`step ${currentStep === idx ? "active" : ""}`}>
                    <div className="step-number">{idx + 1}</div>
                    <p>{label}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit(handleSubmitLessons)} className="form">
                {currentStep === 0 && (
                  <div className="form-section">
                    <h3>Course Details</h3>
                    <TextField
                      fullWidth
                      label="Course Title"
                      {...register("title", { required: "Title is required" })}
                      value={courseData.title}
                      onChange={(e) => handleCourseChange("title", e.target.value)}
                      variant="outlined"
                      className="form-input"
                      aria-invalid={errors.title ? "true" : "false"}
                    />
                    {errors.title && <p className="error-text">{errors.title.message}</p>}
                    <TextField
                      fullWidth
                      label="Description"
                      {...register("description", { required: "Description is required" })}
                      value={courseData.description}
                      onChange={(e) => handleCourseChange("description", e.target.value)}
                      variant="outlined"
                      multiline
                      rows={3}
                      className="form-input"
                      aria-invalid={errors.description ? "true" : "false"}
                    />
                    {errors.description && <p className="error-text">{errors.description.message}</p>}
                    <div className="form-group">
                      <label>Thumbnail</label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => handleCourseChange("thumbnail", e)}
                        className="form-file"
                        aria-required="true"
                      />
                      {courseData.thumbnail && typeof courseData.thumbnail === "string" && (
                        <p className="current-file">Current: {courseData.thumbnail.split("/").pop()}</p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Course Type</label>
                      <RadioGroup
                        row
                        value={courseData.is_paid ? "paid" : "free"}
                        onChange={(e) => handleCourseChange("is_paid", e.target.value === "paid")}
                        className="paid-free-radio"
                        aria-label="Course payment type"
                      >
                        <FormControlLabel value="free" control={<Radio id="course-type-free" />} label="Free" />
                        <FormControlLabel value="paid" control={<Radio id="course-type-paid" />} label="Paid" />
                      </RadioGroup>
                    </div>
                    {courseData.is_paid && (
                      <div className="form-group price-input">
                        <TextField
                          fullWidth
                          label="Course Price"
                          type="number"
                          {...register("price", {
                            required: "Price is required for paid courses",
                            min: { value: 0, message: "Price cannot be negative" },
                            valueAsNumber: true,
                          })}
                          value={courseData.price}
                          onChange={(e) => handleCourseChange("price", e.target.value)}
                          variant="outlined"
                          className="form-input"
                          inputProps={{ min: 0, step: "0.01" }}
                          aria-label="Course price in dollars"
                        />
                        {errors.price && <p className="error-text">{errors.price.message}</p>}
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 1 && selectedLessonIdx !== null && lessons[selectedLessonIdx] && (
                  <div className="form-section">
                    <h3>Lesson {selectedLessonIdx + 1}: {lessons[selectedLessonIdx].lesson_name}</h3>
                    <TextField
                      fullWidth
                      label="Lesson Name"
                      value={lessons[selectedLessonIdx].lesson_name}
                      onChange={(e) => handleLessonChange(selectedLessonIdx, "lesson_name", e.target.value)}
                      variant="outlined"
                      className="form-input"
                    />
                    <TextField
                      fullWidth
                      label="Description"
                      value={lessons[selectedLessonIdx].description}
                      onChange={(e) => handleLessonChange(selectedLessonIdx, "description", e.target.value)}
                      variant="outlined"
                      multiline
                      rows={3}
                      className="form-input"
                    />
                    <div className="form-group">
                      <label>Thumbnail</label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => handleLessonChange(selectedLessonIdx, "thumbnail", e)}
                        className="form-file"
                      />
                      {lessons[selectedLessonIdx].thumbnail && typeof lessons[selectedLessonIdx].thumbnail === "string" && (
                        <p className="current-file">Current: {lessons[selectedLessonIdx].thumbnail.split("/").pop()}</p>
                      )}
                    </div>
                  </div>
                )}

                {currentStep === 2 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
                  <div className="form-section">
                    <h3>Subject {selectedSubjectIdx + 1}: {lessons[selectedLessonIdx].subjects[selectedSubjectIdx].chapter_name}</h3>
                    <TextField
                      fullWidth
                      label="Subject Name"
                      value={lessons[selectedLessonIdx].subjects[selectedSubjectIdx].chapter_name}
                      onChange={(e) =>
                        handleSubjectChange(selectedLessonIdx, selectedSubjectIdx, "chapter_name", e.target.value)
                      }
                      variant="outlined"
                      className="form-input"
                    />
                    <TextField
                      fullWidth
                      label="Description"
                      value={lessons[selectedLessonIdx].subjects[selectedSubjectIdx].description}
                      onChange={(e) =>
                        handleSubjectChange(selectedLessonIdx, selectedSubjectIdx, "description", e.target.value)
                      }
                      variant="outlined"
                      multiline
                      rows={3}
                      className="form-input"
                    />
                    <div className="form-group">
                      <label>Thumbnail</label>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={(e) => handleSubjectChange(selectedLessonIdx, selectedSubjectIdx, "thumbnail", e)}
                        className="form-file"
                      />
                      {lessons[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail &&
                        typeof lessons[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail === "string" && (
                          <p className="current-file">
                            Current: {lessons[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail.split("/").pop()}
                          </p>
                        )}
                    </div>
                  </div>
                )}

                {currentStep === 3 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
                  <div className="form-section content-section">
                    <h3>Add Content for Subject {selectedSubjectIdx + 1}</h3>
                    <div className="content-types">
                      {[
                        { type: "pdf", label: "PDF", icon: faFilePdf },
                        { type: "video", label: "Video", icon: faFilm },
                        { type: "image", label: "Image", icon: faImage },
                        { type: "test", label: "MCQ", icon: faPen },
                      ].map(({ type, label, icon }) => (
                        <div
                          key={type}
                          className="content-type-card"
                          onClick={() => addContent(type)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                          }}
                        >
                          <FontAwesomeIcon icon={icon} className="content-icon" />
                          <span>Add {label}</span>
                        </div>
                      ))}
                    </div>
                    {showVideoPrompt && (
                      <div className="video-input-options">
                        <label className="video-option-label">Select Video Source</label>
                        <div className="radio-group">
                          <label>
                            <input
                              type="radio"
                              checked={!isYouTube}
                              onChange={() => {
                                setIsYouTube(false);
                                setYouTubeUrl("");
                                setYouTubeError("");
                              }}
                            />
                            Upload Video File
                          </label>
                          <label>
                            <input
                              type="radio"
                              checked={isYouTube}
                              onChange={() => setIsYouTube(true)}
                            />
                            YouTube Link
                          </label>
                        </div>
                        {isYouTube ? (
                          <div className="youtube-input">
                            <TextField
                              fullWidth
                              label="YouTube URL"
                              value={youTubeUrl}
                              onChange={(e) => {
                                setYouTubeUrl(e.target.value);
                                setYouTubeError("");
                              }}
                              placeholder="https://www.youtube.com/watch?v=..."
                              variant="outlined"
                              className="form-input"
                            />
                            {youTubeError && <p className="error-text">{youTubeError}</p>}
                            <div className="video-prompt-actions">
                              <Button variant="primary" onClick={addVideoContent} className="youtube-btn">
                                Add YouTube Video
                              </Button>
                              <Button variant="outline-secondary" onClick={() => setShowVideoPrompt(false)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="video-upload-section">
                            <input
                              type="file"
                              ref={fileInputRef}
                              accept=".mp4,.mov"
                              multiple
                              onChange={handleFileInputChange}
                              style={{ display: "none" }}
                            />
                            <div className="video-prompt-actions">
                              <Button
                                variant="primary"
                                onClick={addVideoContent}
                              >
                                Select Video Files
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() => setShowVideoPrompt(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="content-list">
                      {["pdf", "video", "image", "test"].map((type) => {
                        const files = lessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type];
                        return files.length > 0 ? (
                          <div key={type} className="content-group">
                            <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
                            <div className="content-files">
                              {files.map((file, i) => (
                                <div key={i} className="content-file">
                                  {type === "video" && file.type === "youtube" ? (
                                 <a
                                 href={`https://www.youtube.com/watch?v=${getYouTubeVideoId(file.url)}`}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 style={{
                                   position: 'relative',
                                   display: 'inline-block',
                                   width: '100px',
                                   height: '60px',
                                   textDecoration: 'none'
                                 }}
                               >
                                 <img
                                   src={`https://img.youtube.com/vi/${getYouTubeVideoId(file.url)}/0.jpg`}
                                   alt={file.name}
                                   style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                 />
                                 <FontAwesomeIcon
                                   icon={faYoutube}
                                   style={{
                                     position: 'absolute',
                                     top: '50%',
                                     left: '50%',
                                     transform: 'translate(-50%, -50%)',
                                     color: 'red',
                                     fontSize: '30px',
                                     borderRadius: '50%',
                                     padding: '4px'
                                   }}
                                 />
                               </a>
                                  ) : type === "image" ? (
                                    <img src={file.url} alt={file.name} className="content-preview" />
                                  ) : type === "test" ? (
                                    <div className="test-contentCreateCourse" style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      gap: '10px'
                                    }}>
                                      <span style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '70%', // or any appropriate width
                                        display: 'inline-block'
                                      }}>
                                        {file.name}
                                      </span>
                                    
                                      <div className="content-actions" style={{ display: 'flex', gap: '4px' }}>
                                        <IconButton
                                          onClick={() => updateTest(i)}
                                          aria-label={`Edit test ${file.name}`}
                                        >
                                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '18px' }} />
                                        </IconButton>
                                        <IconButton
                                          onClick={() => deleteTest(i)}
                                          aria-label={`Delete test ${file.name}`}
                                        >
                                          <MdDelete size={20} color="#dc3545" />
                                        </IconButton>
                                      </div>
                                    </div>
                                    
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={type === "pdf" ? faFilePdf : type === "video" ? faFilm : faPen}
                                      className="content-icon"
                                    />
                                  )}
                                  {type !== "test" && <span>{file.name}</span>}
                                  {type !== "test" && (
                                    <IconButton onClick={() => deleteContent(type, i)} aria-label={`Delete ${type} content ${file.name}`}>
                                      <MdDelete size={16} color="#dc3545" />
                                    </IconButton>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })}
                      {["pdf", "video", "image", "test"].every(
                        (type) => lessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].length === 0
                      ) && <p className="no-data">No content added yet.</p>}
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  {currentStep > 0 && (
                    <button
                      variant="outline-secondary"
                      onClick={() => {
                        if (currentStep === 1) setCurrentStep(0);
                        else if (currentStep === 2) setCurrentStep(1);
                        else if (currentStep === 3) setCurrentStep(2);
                      }}
                      className="back-btn"
                    >
                      Back
                    </button>
                  )}
                  <div className="action-buttons">
                    {currentStep === 0 && (
                      <button variant="outline-primary" onClick={() => setCurrentStep(1)} className="action-btn">
                        Edit Lessons
                      </button>
                    )}
                    {currentStep === 1 && (
                      <button variant="outline-primary" onClick={() => addSubject(selectedLessonIdx)} className="action-btn">
                        + Create New Subject
                      </button>
                    )}
                    {currentStep === 2 && (
                      <button variant="outline-primary" onClick={() => setCurrentStep(3)} className="action-btn">
                        Add Content
                      </button>
                    )}
                     <button type="submit" className="submit-btn">
                      Update Course
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Test Modal */}
        {showTestModal && (
                    <Dialog
                      open={showTestModal}
                      // onClose={() => setShowTestModal(false)}
                      maxWidth="md"
                      PaperProps={{ className: "test-modal" }}
                    >
                      <div className="test-modal-content">
                        <div className="test-modal-header">
                          <h2>Create/Edit Tests</h2>
                          <IoCloseCircleOutline
                            size={28}
                            onClick={() => setShowTestModal(false)}
                            style={{ cursor: "pointer", color: "#f44336" }}
                          />
                        </div>
                        <div className="test-modal-body">
                          <div className="test-group-form">
                            <h3>{editingTestGroupIdx !== null ? "Edit Test Name" : "Add Test Name"}</h3>
                            <form onSubmit={handleTestGroupSubmit(addTestGroup)}>
                              <div className="form-group">
                                <label>Test Name</label>
                                <input
                                  {...registerTestGroup("testGroupName", { required: "Test group name is required" })}
                                  className="form-input"
                                  aria-invalid={testGroupErrors.testGroupName ? "true" : "false"}
                                />
                                {testGroupErrors.testGroupName && <p className="error-text">{testGroupErrors.testGroupName.message}</p>}
                              </div>
                              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
  <button type="submit" className="action-btn">
    {editingTestGroupIdx !== null ? "Update Test Name" : "Add Test Name"}
  </button>
  {editingTestGroupIdx !== null && (
    <button
      type="button"
      onClick={() => {
        setEditingTestGroupIdx(null);
        resetTestGroup();
      }}
      className="cancel-btn"
    >
      Cancel Edit
    </button>
  )}
</div>

                            </form>
                          </div>
                          {testGroups.length > 0 && (
                            <div className="test-group-list">
                              <h3>Test Names</h3>
                              <ul>
                                {testGroups.map((group, idx) => (
                                  <li
                                    key={idx}
                                    className={`test-group-item ${selectedTestGroupIdx === idx ? 'active' : ''}`}
                                    onClick={() => setSelectedTestGroupIdx(idx)}
                                  >
                                    <span>{group.name}</span>
                                    <div className="test-group-actions">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          editTestGroup(idx);
                                        }}
                                        className="edit-btn"
                                        title="Edit test group"
                                      >
                                        <FontAwesomeIcon icon={faEdit} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteTestGroup(idx);
                                        }}
                                        className="delete-btnTest"
                                        title="Delete test group"
                                      >
                                        <IoTrashOutline />
                                      </button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {selectedTestGroupIdx !== null && (
                            <div className="question-form">
                              <h3>
                                {editingQuestionIdx !== null
                                  ? `Edit Question in ${testGroups[selectedTestGroupIdx].name}`
                                  : `Add Questions to ${testGroups[selectedTestGroupIdx].name}`}
                              </h3>
                              <form onSubmit={handleQuestionSubmit(addQuestion)}>
                                <div className="form-group">
                                  <label>Question</label>
                                  <input
                                    {...registerQuestion("question", { required: "Question is required" })}
                                    className="form-input"
                                    aria-invalid={questionErrors.question ? "true" : "false"}
                                  />
                                  {questionErrors.question && <p className="error-text">{questionErrors.question.message}</p>}
                                </div>
                                <div className="form-group">
                                  <label>Options</label>
                                  {Array(5)
                                    .fill()
                                    .map((_, i) => (
                                      <div key={i} className="option-input">
                                        <input
                                          {...registerQuestion(`options.${i}`, {
                                            required: `Option ${i + 1} is required`,
                                          })}
                                          className="form-input"
                                          placeholder={`Option ${i + 1}`}
                                          aria-invalid={questionErrors.options?.[i] ? "true" : "false"}
                                        />
                                        {questionErrors.options?.[i] && (
                                          <p className="error-text">{questionErrors.options[i].message}</p>
                                        )}
                                      </div>
                                    ))}
                                </div>
                                <div className="form-group">
                                  <label>Correct Answer</label>
                                  <select
                                    {...registerQuestion("answer", { required: "Correct answer is required" })}
                                    className="form-input"
                                    aria-invalid={questionErrors.answer ? "true" : "false"}
                                    value={watchQuestion("answer") || ""}
                                  >
                                    <option value="">Select correct answer</option>
                                    {watchQuestion("options")?.map((option, i) => (
                                      <option key={i} value={option} disabled={!option}>
                                        {option || `Option ${i + 1} (empty)`}
                                      </option>
                                    ))}
                                  </select>
                                  {questionErrors.answer && <p className="error-text">{questionErrors.answer.message}</p>}
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
  <button type="submit" className="action-btn">
    {editingQuestionIdx !== null ? "Update Question" : "Add Question"}
  </button>
  {editingQuestionIdx !== null && (
    <button
      type="button"
      onClick={() => {
        setEditingQuestionIdx(null);
        resetQuestion({
          question: "",
          options: ["", "", "", "", ""],
          answer: ""
        });
      }}
      className="cancel-btn"
    >
      Cancel Edit
    </button>
  )}
</div>

                              </form>
                              {testGroups[selectedTestGroupIdx].tests.length > 0 && (
                                <div className="question-list">
                                  <h4>Questions</h4>
                                  <ul>
                                    {testGroups[selectedTestGroupIdx].tests.map((test, qIdx) => (
                                      <li key={qIdx} className="question-item">
                                        <span>{test.question}</span>
                                        <div className="question-actions">
                                          <button
                                            type="button"
                                            onClick={() => editQuestion(qIdx)}
                                            className="edit-btn"
                                            title="Edit question"
                                          >
                                            <FontAwesomeIcon icon={faEdit} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => deleteQuestion(qIdx)}
                                            className="delete-btnTest"
                                            title="Delete question"
                                          >
                                            <IoTrashOutline />
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="test-modal-actions">
                          <button type="button" onClick={saveTests} className="action-btn">Save Tests</button>
                          <button type="button" onClick={() => setShowTestModal(false)} className="cancel-btn">Cancel</button>
                        </div>
                      </div>
                    </Dialog>
                  )}
        </>
      )}
    </Dialog>
  );
};

export default CreateCourseDialog;






// **************************************************************Without Test pop up************************************************************************
// import React, { useEffect, useState, useRef } from "react";
// import { Button } from "react-bootstrap";
// import { useDispatch, useSelector } from "react-redux";
// import { useForm } from "react-hook-form";
// import {
//   openCreateCourseDialog,
//   popUpDashboard,
//   updateCourse,
//   getAllCourses
// } from "../Redux/Dashboard/DashboardSlice";
// import { Notify } from "../CommonPopup/DashboardPopUp";
// import { useNavigate } from "react-router-dom";
// import Dialog from "@mui/material/Dialog";
// import { useTheme } from "@mui/material/styles";
// import { useMediaQuery, TextField, IconButton, Radio, RadioGroup, FormControlLabel } from "@mui/material";
// import { IoCloseCircleOutline, IoAdd } from "react-icons/io5";
// import { MdDelete } from "react-icons/md";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faFilePdf, faFilm, faImage, faPen, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
// import "../Style/CreateCourseDialog.css";
// import Spinner from '../Spinner/Spinner'

// const CreateCourseDialog = () => {
//   const theme = useTheme();
//   const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { register, handleSubmit, reset, setValue, formState: { errors }, getValues } = useForm({
//     defaultValues: {
//       title: "",
//       description: "",
//       price: 0,
//     },
//   });

//   const {
//     loader,
//     showCreateCourseDialog,
//     selectedCourseFolder,
//     allCoursesLessons = [],
//   } = useSelector((s) => s.DashboardSlice);
//   const courseId = selectedCourseFolder?.course?.course?._id;

//   const [lessons, setLessons] = useState([]);
//   const [deletedContent, setDeletedContent] = useState([]);
//   const [selectedLessonIdx, setSelectedLessonIdx] = useState(null);
//   const [selectedSubjectIdx, setSelectedSubjectIdx] = useState(null);
//   const [currentStep, setCurrentStep] = useState(0);
//   const [showSidebar, setShowSidebar] = useState(true);
//   const [showVideoPrompt, setShowVideoPrompt] = useState(false);
//   const [isYouTube, setIsYouTube] = useState(false);
//   const [youTubeUrl, setYouTubeUrl] = useState("");
//   const [youTubeError, setYouTubeError] = useState("");
//   const [uploading, setUploading] = useState(false);
//   const [courseData, setCourseData] = useState({
//     title: "",
//     description: "",
//     thumbnail: null,
//     is_paid: false,
//     price: 0,
//   });
//   const [activeButtonId, setActiveButtonId] = useState(null);
//   const fileInputRef = useRef(null);

//   useEffect(() => {
//     if (selectedCourseFolder?.course?.course) {
//       const course = selectedCourseFolder.course.course;
//       setCourseData({
//         title: course.title || "",
//         description: course.description || "",
//         thumbnail: course.thumbnail || null,
//         is_paid: course.is_paid || false,
//         price: course.is_paid ? course.price || 0 : 0,
//       });
//       setValue("title", course.title || "");
//       setValue("description", course.description || "");
//       setValue("price", course.is_paid ? course.price || 0 : 0);
//       console.log("Loaded course data:", { is_paid: course.is_paid, price: course.price });
//       if (course.lessons) {
//         const mappedLessons = course.lessons.map(lesson => ({
//           _id: lesson._id,
//           lesson_name: lesson.lesson_name,
//           description: lesson.description || "",
//           thumbnail: lesson.thumbnail || null,
//           subjects: lesson.subjects?.map(subject => ({
//             _id: subject._id,
//             chapter_name: subject.chapter_name,
//             description: subject.description || "",
//             thumbnail: subject.thumbnail || null,
//             content: subject.content || { pdf: [], video: [], image: [], test: [] }
//           })) || []
//         }));
//         setLessons(structuredClone(mappedLessons));
//         if (mappedLessons.length > 0) {
//           setSelectedLessonIdx(0);
//           setCurrentStep(1);
//         }
//       }
//     }
//     dispatch(getAllCourses({ isPaid: "" }));
//   }, [selectedCourseFolder, dispatch, setValue]);

//   const getYouTubeVideoId = (url) => {
//     try {
//       const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
//       const match = url.match(regex);
//       if (!match || !match[1]) {
//         throw new Error("Invalid YouTube URL format");
//       }
//       return match[1];
//     } catch (error) {
//       console.error("Error parsing YouTube URL:", error);
//       return null;
//     }
//   };

//   const fetchYouTubeVideoTitle = async (url) => {
//     try {
//       const response = await fetch(`https://noembed.com/embed?format=json&url=${encodeURIComponent(url)}`);
//       const data = await response.json();
//       if (data.error) {
//         throw new Error(data.error);
//       }
//       return data.title || null;
//     } catch (error) {
//       console.error('Error fetching YouTube video title:', error);
//       return null;
//     }
//   };

//   const handleClose = () => {
//     reset({ title: "", description: "", price: 0 });
//     setLessons([]);
//     setDeletedContent([]);
//     setSelectedLessonIdx(null);
//     setSelectedSubjectIdx(null);
//     setCurrentStep(0);
//     setShowSidebar(true);
//     setShowVideoPrompt(false);
//     setIsYouTube(false);
//     setYouTubeUrl("");
//     setYouTubeError("");
//     setUploading(false);
//     setCourseData({ title: "", description: "", thumbnail: null, is_paid: false, price: 0 });
//     setActiveButtonId(null);
//     dispatch(openCreateCourseDialog(false));
//   };

//   const addLesson = () => {
//     const values = getValues();
//     const hasErrors = Object.keys(errors).length > 0;
//     const isTitleValid = values.title && values.title.trim() !== "";
//     const isDescriptionValid = values.description && values.description.trim() !== "";
//     const isThumbnailValid = courseData.thumbnail !== null && (courseData.thumbnail instanceof File || typeof courseData.thumbnail === "string");
//     const isPriceValid = !courseData.is_paid || (values.price >= 0);

//     if (!isTitleValid || !isDescriptionValid || !isThumbnailValid || !isPriceValid || hasErrors) {
//       const errorMessages = [];
//       if (!isTitleValid || errors.title) errorMessages.push("Title is required");
//       if (!isDescriptionValid || errors.description) errorMessages.push("Description is required");
//       if (!isThumbnailValid) errorMessages.push("Thumbnail is required");
//       if (!isPriceValid || errors.price) errorMessages.push("Valid price is required for paid courses");

//       Notify({
//         data: "error",
//         msg: `Please fill all course details: ${errorMessages.join(", ")}.`,
//         type: "error",
//       });
//       console.log("Lesson add failed:", {
//         title: isTitleValid ? "Valid" : "Invalid",
//         description: isDescriptionValid ? "Valid" : "Invalid",
//         thumbnail: isThumbnailValid ? "Valid" : "Invalid",
//         price: isPriceValid ? values.price : "Invalid",
//       });
//       return;
//     }

//     const newLesson = {
//       lesson_name: `Lesson ${lessons.length + 1}`,
//       description: "",
//       thumbnail: null,
//       subjects: [],
//     };
//     setLessons([...lessons, structuredClone(newLesson)]);
//     setSelectedLessonIdx(lessons.length);
//     setSelectedSubjectIdx(null);
//     setCurrentStep(1);
//     Notify({ data: "success", msg: "Lesson added successfully", type: "success" });
//     console.log("Added lesson:", newLesson);
//   };

//   const addSubject = (lessonIdx, navigate = true) => {
//     if (lessonIdx === null || lessonIdx < 0 || lessonIdx >= lessons.length) {
//       console.error("Invalid lesson index:", lessonIdx);
//       return;
//     }
//     const updatedLessons = structuredClone(lessons);
//     updatedLessons[lessonIdx].subjects.push({
//       chapter_name: `Subject ${updatedLessons[lessonIdx].subjects.length + 1}`,
//       description: "",
//       thumbnail: null,
//       content: { pdf: [], video: [], image: [], test: [] },
//     });
//     setLessons(updatedLessons);
//     if (navigate) {
//       setSelectedLessonIdx(lessonIdx);
//       setSelectedSubjectIdx(updatedLessons[lessonIdx].subjects.length - 1);
//       setCurrentStep(2);
//     }
//     console.log("Added subject to lesson:", lessonIdx, updatedLessons[lessonIdx].subjects);
//   };

//   const deleteLesson = (index, e) => {
//     e.stopPropagation();
//     console.log("Deleting lesson:", { index, lessonsBefore: lessons });
//     const updatedLessons = lessons.filter((_, i) => i !== index);
//     setLessons(updatedLessons);
//     if (selectedLessonIdx === index) {
//       setSelectedLessonIdx(null);
//       setSelectedSubjectIdx(null);
//       setCurrentStep(0);
//     }
//     Notify({ data: "success", msg: `Lesson ${index + 1} deleted successfully`, type: "success" });
//     console.log("Lesson deleted:", { index, lessonsAfter: updatedLessons });
//   };

//   const deleteSubject = (lessonIdx, subjectIdx, e) => {
//     e.stopPropagation();
//     console.log("Deleting subject:", { lessonIdx, subjectIdx, lessonsBefore: lessons });
//     if (!lessons[lessonIdx]) {
//       console.error("Invalid lesson index:", lessonIdx);
//       Notify({ data: "error", msg: "Cannot delete subject: Invalid lesson", type: "error" });
//       return;
//     }
//     const updatedLessons = structuredClone(lessons);
//     updatedLessons[lessonIdx].subjects = updatedLessons[lessonIdx].subjects.filter((_, i) => i !== subjectIdx);
//     setLessons(updatedLessons);
//     if (selectedLessonIdx === lessonIdx && selectedSubjectIdx === subjectIdx) {
//       setSelectedSubjectIdx(null);
//       setCurrentStep(1);
//     }
//     Notify({ data: "success", msg: `Subject ${subjectIdx + 1} deleted successfully`, type: "success" });
//     console.log("Subject deleted:", { lessonIdx, subjectIdx, lessonsAfter: updatedLessons });
//   };

//   const deleteContent = (type, index) => {
//     if (selectedLessonIdx === null || selectedSubjectIdx === null) {
//       console.error("Invalid selection:", { selectedLessonIdx, selectedSubjectIdx });
//       return;
//     }
//     console.log("Deleting content:", { type, index });
//     const updatedLessons = structuredClone(lessons);
//     const contentItem = updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type][index];
//     if (contentItem._id) {
//       setDeletedContent(prev => [...prev, { _id: contentItem._id, type }]);
//     }
//     updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type] =
//       updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].filter((_, i) => i !== index);
//     setLessons(updatedLessons);
//     Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} removed successfully`, type: "success" });
//     console.log("Content deleted:", { type, index, deletedContentId: contentItem._id, lessonsAfter: updatedLessons });
//   };

//   const validateImageFile = (file) => {
//     if (!file) return false;
//     const validTypes = ["image/jpeg", "image/jpg", "image/png"];
//     return validTypes.includes(file.type);
//   };

//   const handleCourseChange = (field, value) => {
//     if (field === "thumbnail") {
//       const file = value.target.files[0];
//       if (file && !validateImageFile(file)) {
//         Notify({
//           data: "error",
//           msg: "Invalid thumbnail format. Please upload a .jpg, .jpeg, or .png image.",
//           type: "error",
//         });
//         return;
//       }
//     }
//     setCourseData((prev) => {
//       const newData = {
//         ...prev,
//         [field]: field === "thumbnail" ? value.target.files[0] : field === "is_paid" ? value : field === "price" ? parseFloat(value) || 0 : value,
//       };
//       if (field === "price") {
//         setValue("price", parseFloat(value) || 0);
//       } else if (field === "title") {
//         setValue("title", value);
//       } else if (field === "description") {
//         setValue("description", value);
//       } else if (field === "is_paid") {
//         setValue("price", value ? prev.price : 0);
//       }
//       console.log("Course data changed:", { field, value: field === "thumbnail" ? value.target.files[0]?.name : value });
//       return newData;
//     });
//   };

//   const handleLessonChange = (index, field, value) => {
//     if (field === "thumbnail") {
//       const file = value.target.files[0];
//       if (file && !validateImageFile(file)) {
//         Notify({
//           data: "error",
//           msg: "Invalid thumbnail format. Please upload a .jpg, .jpeg, or .png image.",
//           type: "error",
//         });
//         return;
//       }
//     }
//     const updatedLessons = structuredClone(lessons);
//     updatedLessons[index][field] = field === "thumbnail" ? value.target.files[0] : value;
//     setLessons(updatedLessons);
//     console.log("Lesson changed:", { index, field, value: field === "thumbnail" ? value.target.files[0]?.name : value });
//   };

//   const handleSubjectChange = (lessonIndex, subjectIndex, field, value) => {
//     if (field === "thumbnail") {
//       const file = value.target.files[0];
//       if (file && !validateImageFile(file)) {
//         Notify({
//           data: "error",
//           msg: "Invalid thumbnail format. Please upload a .jpg, .jpeg, or .png image.",
//           type: "error",
//         });
//         return;
//       }
//     }
//     const updatedLessons = structuredClone(lessons);
//     updatedLessons[lessonIndex].subjects[subjectIndex][field] =
//       field === "thumbnail" ? value.target.files[0] : value;
//     setLessons(updatedLessons);
//     console.log("Subject changed:", { lessonIndex, subjectIndex, field, value: field === "thumbnail" ? value.target.files[0]?.name : value });
//   };

//   const addVideoContent = async () => {
//     if (selectedLessonIdx === null || selectedSubjectIdx === null) {
//       console.error("Invalid selection for adding video:", { selectedLessonIdx, selectedSubjectIdx });
//       Notify({ data: "error", msg: "Please select a subject to add video content", type: "error" });
//       return;
//     }

//     if (isYouTube) {
//       if (!youTubeUrl) {
//         setYouTubeError("Please enter a YouTube URL.");
//         return;
//       }
//       const videoId = getYouTubeVideoId(youTubeUrl);
//       if (!videoId) {
//         setYouTubeError("Invalid YouTube URL.");
//         return;
//       }
//       const videoTitle = await fetchYouTubeVideoTitle(youTubeUrl);
//       if (!videoTitle) {
//         setYouTubeError("Failed to fetch video title. Please try again.");
//         return;
//       }
//       const updatedLessons = structuredClone(lessons);
//       const videoContent = updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content.video;
//       videoContent.push({
//         name: videoTitle,
//         url: youTubeUrl,
//         type: "youtube",
//       });
//       setLessons(updatedLessons);
//       setYouTubeUrl("");
//       setYouTubeError("");
//       setShowVideoPrompt(false);
//       Notify({ data: "success", msg: "YouTube video added successfully", type: "success" });
//       console.log("Added YouTube video:", { title: videoTitle, url: youTubeUrl, lessonsAfter: updatedLessons });
//     } else {
//       if (!fileInputRef.current) {
//         console.error("File input ref is not available");
//         Notify({ data: "error", msg: "Failed to open file picker", type: "error" });
//         return;
//       }
//       setUploading(true);
//       fileInputRef.current.value = null;
//       fileInputRef.current.click();
//     }
//   };

//   const handleFileInputChange = (e) => {
//     console.log("Handling file input change");
//     const files = Array.from(e.target.files);
//     console.log("Selected files:", files.map(f => f.name));
//     if (files.length === 0) {
//       setUploading(false);
//       Notify({ data: "warning", msg: "No video files selected", type: "warning" });
//       return;
//     }

//     const validFiles = files.filter((file) => {
//       const isValidType = ["video/mp4", "video/quicktime"].includes(file.type);
//       if (!isValidType) {
//         Notify({ data: "error", msg: `Invalid file type: ${file.name}. Please select .mp4 or .mov files.`, type: "error" });
//       }
//       return isValidType;
//     });

//     if (validFiles.length === 0) {
//       setUploading(false);
//       Notify({ data: "error", msg: "No valid video files selected", type: "error" });
//       return;
//     }

//     const updatedLessons = structuredClone(lessons);
//     const newFiles = validFiles.map((file) => ({
//       name: file.name,
//       url: URL.createObjectURL(file),
//       fileData: file,
//       type: "file",
//     }));
//     updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content.video.push(...newFiles);
//     setLessons(updatedLessons);
//     setShowVideoPrompt(false);
//     setUploading(false);
//     Notify({ data: "success", msg: `Added ${validFiles.length} video file(s) successfully`, type: "success" });
//     console.log("Added video files:", { files: newFiles.map(f => f.name), lessonsAfter: updatedLessons });
//   };

//   const addContent = (type) => {
//     if (selectedLessonIdx === null || selectedSubjectIdx === null) {
//       console.error("Invalid selection for adding content:", { selectedLessonIdx, selectedSubjectIdx });
//       Notify({ data: "error", msg: "Please select a subject to add content", type: "error" });
//       return;
//     }

//     if (type === "video") {
//       setShowVideoPrompt(true);
//       setIsYouTube(false);
//       setYouTubeUrl("");
//       setYouTubeError("");
//       return;
//     }

//     const input = document.createElement("input");
//     input.type = "file";
//     input.multiple = true;
//     input.accept = type === "pdf" ? ".pdf" : type === "image" ? ".jpg,.jpeg,.png" : ".pdf";
//     input.onchange = (e) => {
//       const files = Array.from(e.target.files);
//       console.log(`Selected ${type} files:`, files.map(f => f.name));
//       if (files.length === 0) {
//         Notify({ data: "warning", msg: `No ${type} files selected`, type: "warning" });
//         return;
//       }

//       const validFiles = files.filter((file) => {
//         const isValidType = type === "pdf" || type === "test" ? file.type === "application/pdf" : ["image/jpeg", "image/png"].includes(file.type);
//         if (!isValidType) {
//           Notify({ data: "error", msg: `Invalid file type: ${file.name}`, type: "error" });
//           return false;
//         }
//         return true;
//       });

//       if (validFiles.length === 0) {
//         Notify({ data: "error", msg: `No valid ${type} files selected`, type: "error" });
//         return;
//       }

//       const updatedLessons = structuredClone(lessons);
//       const newFiles = validFiles.map((file) => ({
//         name: file.name,
//         url: URL.createObjectURL(file),
//         fileData: file,
//         type: "file",
//       }));
//       updatedLessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].push(...newFiles);
//       setLessons(updatedLessons);
//       Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} file(s) added successfully`, type: "success" });
//       console.log("Added content:", { type, files: newFiles.map(f => f.name), lessonsAfter: updatedLessons });
//     };
//     input.click();
//   };

//   const handleSelectSubject = (lessonIdx, subjectIdx) => {
//     setSelectedLessonIdx(lessonIdx);
//     setSelectedSubjectIdx(subjectIdx);
//     setCurrentStep(2);
//     console.log("Selected subject:", { lessonIdx, subjectIdx });
//   };

//   const handleSelectContent = (lessonIdx, subjectIdx, e) => {
//     e.stopPropagation();
//     setSelectedLessonIdx(lessonIdx);
//     setSelectedSubjectIdx(subjectIdx);
//     setCurrentStep(3);
//     console.log("Navigated to content for:", { lessonIdx, subjectIdx });
//   };

//   const handleAdd = (menuId, e) => {
//     e.stopPropagation();
//     setActiveButtonId(menuId);
//     setTimeout(() => setActiveButtonId(null), 500);
//     console.log("Handling add for:", menuId);
//     if (menuId.startsWith("lesson-")) {
//       const lessonIdx = parseInt(menuId.split("-")[1], 10);
//       addSubject(lessonIdx, true);
//     }
//   };

//   const handleSubmitLessons = async (data) => {
//     if (!courseId) {
//       Notify({ data: "error", msg: "Course ID missing!", type: "error" });
//       return;
//     }

//     const formData = new FormData();

//     formData.append("title", courseData.title);
//     formData.append("description", courseData.description);
//     formData.append("is_paid", courseData.is_paid);
//     formData.append("price", courseData.is_paid ? getValues("price") : 0);

//     if (courseData.thumbnail instanceof File) {
//       formData.append("files", courseData.thumbnail, courseData.thumbnail.name);
//       formData.append("thumbnail", courseData.thumbnail.name);
//     } else if (typeof courseData.thumbnail === "string") {
//       formData.append("thumbnail", courseData.thumbnail);
//     }

//     const structuredLessons = lessons.map((lesson) => {
//       if (lesson.thumbnail instanceof File) {
//         formData.append("files", lesson.thumbnail, lesson.thumbnail.name);
//       }

//       return {
//         _id: lesson._id || undefined,
//         lesson_name: lesson.lesson_name,
//         description: lesson.description,
//         thumbnail: lesson.thumbnail instanceof File ? lesson.thumbnail.name : lesson.thumbnail || "",
//         subjects: lesson.subjects.map((subject) => {
//           if (subject.thumbnail instanceof File) {
//             formData.append("files", subject.thumbnail, subject.thumbnail.name);
//           }

//           const content = {};
//           ["pdf", "video", "image", "test"].forEach((type) => {
//             content[type] = subject.content?.[type]?.map((file) => {
//               if (file.type === "youtube") {
//                 return { name: file.name, url: file.url, type: "youtube" };
//               }
//               if (file.fileData instanceof File) {
//                 formData.append("files", file.fileData, file.fileData.name);
//                 return { name: file.fileData.name, url: file.url, type: "s3" };
//               }
//               return file;
//             }) || [];
//           });

//           return {
//             _id: subject._id || undefined,
//             chapter_name: subject.chapter_name,
//             description: subject.description,
//             thumbnail: subject.thumbnail instanceof File ? subject.thumbnail.name : subject.thumbnail || "",
//             content,
//           };
//         }),
//       };
//     });

//     formData.append("lessons", JSON.stringify(structuredLessons));
//     if (deletedContent.length > 0) {
//       formData.append("deletedContent", JSON.stringify(deletedContent));
//     }
//     console.log("Submitting FormData:");
//     for (let [key, value] of formData.entries()) {
//       console.log(`${key}:`, value instanceof File ? value.name : value);
//     }
//     console.log("Submitting deletedContent:", deletedContent);
//     console.log("Submitting is_paid:", courseData.is_paid);
//     console.log("Submitting price:", courseData.is_paid ? getValues("price") : 0);

//     try {
//       const result = await dispatch(updateCourse({ courseId, FormData: formData })).unwrap();
//       console.log("Course update success:", result);
//       Notify({ data: "success", msg: "Course updated successfully", type: "success" });
//       handleClose();
//       dispatch(getAllCourses({ isPaid: "" }));
//       navigate("/courses");
//     } catch (error) {
//       console.error("Course update failed:", error);
//       Notify({ data: "error", msg: error.message || "Failed to update course", type: "error" });
//     }

//     setTimeout(() => dispatch(popUpDashboard(false)), 5000);
//   };

//   const stepLabels = ["Course", "Lesson", "Subject", "Content"];

//   return (
//     <Dialog
//       fullScreen={fullScreen}
//       open={showCreateCourseDialog}
//       onClose={handleClose}
//       maxWidth="lg"
//       PaperProps={{ className: "create-course-dialog" }}
//     >

//       {loader ? (
//         <div
//           style={{
//             display: "flex",
//             justifyContent: "center",
//             alignItems: "center",
//             height: "100%", // Ensures full height of dialog
//             minHeight: "300px", // Fallback height for content-less dialog
//           }}
//         >
//           <Spinner />
//         </div>
//       ) :
//       <>
//        <div className="dialog-content">
//           {/* Sidebar */}
//           {showSidebar && (
//             <div className="sidebar">
//               <div className="sidebar-header">
//                 <h3>Course Structure</h3>
//                 <IconButton onClick={() => setShowSidebar(false)} aria-label="Close sidebar">
//                   <IoCloseCircleOutline size={28} style={{ cursor: "pointer", color: "#f44336" }} />
//                 </IconButton>
//               </div>
//               <div
//                 className={`structure-title ${currentStep === 0 ? "active" : ""}`}
//                 onClick={() => {
//                   setSelectedLessonIdx(null);
//                   setSelectedSubjectIdx(null);
//                   setCurrentStep(0);
//                 }}
//               >
//                 <span>Course Details</span>
//               </div>
//               {lessons.length === 0 ? (
//                 <p className="no-data">No lessons added yet.</p>
//               ) : (
//                 <ul className="structure-list">
//                   {lessons.map((lesson, lIdx) => (
//                     <li key={lIdx} className="structure-item">
//                       <div
//                         className={`structure-title ${selectedLessonIdx === lIdx && currentStep === 1 ? "active" : ""}`}
//                         onClick={() => {
//                           setSelectedLessonIdx(lIdx);
//                           setSelectedSubjectIdx(null);
//                           setCurrentStep(1);
//                         }}
//                       >
//                         <span>{lesson.lesson_name}</span>
//                         <div className="structure-actions">
//                           <IconButton
//                             onClick={(e) => handleAdd(`lesson-${lIdx}`, e)}
//                             className={`add-btnUpdate fw-bold ${activeButtonId === `lesson-${lIdx}` ? "active" : ""}`}
//                             aria-label="Add subject"
//                           >
//                             +
//                           </IconButton>
//                           <IconButton
//                             onClick={(e) => deleteLesson(lIdx, e)}
//                             aria-label={`Delete lesson ${lesson.lesson_name}`}
//                           >
//                             <MdDelete size={16} color="#dc3545" />
//                           </IconButton>
//                         </div>
//                       </div>
//                       {lesson.subjects.length > 0 && (
//                         <ul className="sub-list">
//                           {lesson.subjects.map((subject, sIdx) => (
//                             <li key={sIdx} className="structure-item">
//                               <div
//                                 className={`structure-title ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 2 ? "active" : ""}`}
//                                 onClick={() => handleSelectSubject(lIdx, sIdx)}
//                               >
//                                 <span>{subject.chapter_name}</span>
//                                 <div className="structure-actions">
//                                   <IconButton
//                                     onClick={(e) => handleSelectContent(lIdx, sIdx, e)}
//                                     className={`content-nav-btn ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 3 ? "highlighted" : ""}`}
//                                     aria-label={`View content for ${subject.chapter_name}`}
//                                     title="View content"
//                                   >
//                                     <FontAwesomeIcon icon={faFolderOpen} size="1x" />
//                                   </IconButton>
//                                   <IconButton
//                                     onClick={(e) => deleteSubject(lIdx, sIdx, e)}
//                                     aria-label={`Delete subject ${subject.chapter_name}`}
//                                   >
//                                     <MdDelete size={16} color="#dc3545" />
//                                   </IconButton>
//                                 </div>
//                               </div>
//                             </li>
//                           ))}
//                         </ul>
//                       )}
//                     </li>
//                   ))}
//                 </ul>
//               )}
//               <Button
//                 variant="outline-primary"
//                 onClick={() => {
//                   setActiveButtonId("add-lesson");
//                   setTimeout(() => setActiveButtonId(null), 500);
//                   addLesson();
//                 }}
//                 className={`add-lesson-btn ${activeButtonId === "add-lesson" ? "active" : ""}`}
//                 aria-label="Add new lesson"
//               >
//                 + Add Lesson
//               </Button>
//             </div>
//           )}

//           {/* Main Content */}
//           <div className="main-content">
//             <div className="header">
//               <h2>Edit Course: {courseData.title}</h2>
//               <div className="header-actions">
//                 {!showSidebar && (
//                   <Button variant="link" onClick={() => setShowSidebar(true)} className="show-structure-btn">
//                     Show Course Structure
//                   </Button>
//                 )}
//                 <IconButton onClick={handleClose} aria-label="Close dialog">
//                   <IoCloseCircleOutline size={32} color="#dc3545" />
//                 </IconButton>
//               </div>
//             </div>

//             {/* Step Indicator */}
//             <div className="step-indicator">
//               {stepLabels.map((label, idx) => (
//                 <div key={idx} className={`step ${currentStep === idx ? "active" : ""}`}>
//                   <div className="step-number">{idx + 1}</div>
//                   <p>{label}</p>
//                 </div>
//               ))}
//             </div>

//             <form onSubmit={handleSubmit(handleSubmitLessons)} className="form">
//               {currentStep === 0 && (
//                 <div className="form-section">
//                   <h3>Course Details</h3>
//                   <TextField
//                     fullWidth
//                     label="Course Title"
//                     {...register("title", { required: "Title is required" })}
//                     value={courseData.title}
//                     onChange={(e) => handleCourseChange("title", e.target.value)}
//                     variant="outlined"
//                     className="form-input"
//                     aria-invalid={errors.title ? "true" : "false"}
//                   />
//                   {errors.title && <p className="error-text">{errors.title.message}</p>}
//                   <TextField
//                     fullWidth
//                     label="Description"
//                     {...register("description", { required: "Description is required" })}
//                     value={courseData.title}
//                     onChange={(e) => handleCourseChange("description", e.target.value)}
//                     variant="outlined"
//                     multiline
//                     rows={3}
//                     className="form-input"
//                     aria-invalid={errors.description ? "true" : "false"}
//                   />
//                   {errors.description && <p className="error-text">{errors.description.message}</p>}
//                   <div className="form-group">
//                     <label>Thumbnail</label>
//                     <input
//                       type="file"
//                       accept=".jpg,.jpeg,.png"
//                       onChange={(e) => handleCourseChange("thumbnail", e)}
//                       className="form-file"
//                       aria-required="true"
//                     />
//                     {courseData.thumbnail && typeof courseData.thumbnail === "string" && (
//                       <p className="current-file">Current: {courseData.thumbnail.split("/").pop()}</p>
//                     )}
//                   </div>
//                   <div className="form-group">
//                     <label>Course Type</label>
//                     <RadioGroup
//                       row
//                       value={courseData.is_paid ? "paid" : "free"}
//                       onChange={(e) => handleCourseChange("is_paid", e.target.value === "paid")}
//                       className="paid-free-radio"
//                       aria-label="Course payment type"
//                     >
//                       <FormControlLabel value="free" control={<Radio id="course-type-free" />} label="Free" />
//                       <FormControlLabel value="paid" control={<Radio id="course-type-paid" />} label="Paid" />
//                     </RadioGroup>
//                   </div>
//                   {courseData.is_paid && (
//                     <div className="form-group price-input">
//                       <TextField
//                         fullWidth
//                         label="Course Price"
//                         type="number"
//                         {...register("price", {
//                           required: "Price is required for paid courses",
//                           min: { value: 0, message: "Price cannot be negative" },
//                           valueAsNumber: true,
//                         })}
//                         value={courseData.price}
//                         onChange={(e) => handleCourseChange("price", e.target.value)}
//                         variant="outlined"
//                         className="form-input"
//                         inputProps={{ min: 0, step: "0.01" }}
//                         aria-label="Course price in dollars"
//                       />
//                       {errors.price && <p className="error-text">{errors.price.message}</p>}
//                     </div>
//                   )}
//                 </div>
//               )}

//               {currentStep === 1 && selectedLessonIdx !== null && lessons[selectedLessonIdx] && (
//                 <div className="form-section">
//                   <h3>Lesson {selectedLessonIdx + 1}: {lessons[selectedLessonIdx].lesson_name}</h3>
//                   <TextField
//                     fullWidth
//                     label="Lesson Name"
//                     value={lessons[selectedLessonIdx].lesson_name}
//                     onChange={(e) => handleLessonChange(selectedLessonIdx, "lesson_name", e.target.value)}
//                     variant="outlined"
//                     className="form-input"
//                   />
//                   <TextField
//                     fullWidth
//                     label="Description"
//                     value={lessons[selectedLessonIdx].description}
//                     onChange={(e) => handleLessonChange(selectedLessonIdx, "description", e.target.value)}
//                     variant="outlined"
//                     multiline
//                     rows={3}
//                     className="form-input"
//                   />
//                   <div className="form-group">
//                     <label>Thumbnail</label>
//                     <input
//                       type="file"
//                       accept=".jpg,.jpeg,.png"
//                       onChange={(e) => handleLessonChange(selectedLessonIdx, "thumbnail", e)}
//                       className="form-file"
//                     />
//                     {lessons[selectedLessonIdx].thumbnail && typeof lessons[selectedLessonIdx].thumbnail === "string" && (
//                       <p className="current-file">Current: {lessons[selectedLessonIdx].thumbnail.split("/").pop()}</p>
//                     )}
//                   </div>
//                 </div>
//               )}

//               {currentStep === 2 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
//                 <div className="form-section">
//                   <h3>Subject {selectedSubjectIdx + 1}: {lessons[selectedLessonIdx].subjects[selectedSubjectIdx].chapter_name}</h3>
//                   <TextField
//                     fullWidth
//                     label="Subject Name"
//                     value={lessons[selectedLessonIdx].subjects[selectedSubjectIdx].chapter_name}
//                     onChange={(e) =>
//                       handleSubjectChange(selectedLessonIdx, selectedSubjectIdx, "chapter_name", e.target.value)
//                     }
//                     variant="outlined"
//                     className="form-input"
//                   />
//                   <TextField
//                     fullWidth
//                     label="Description"
//                     value={lessons[selectedLessonIdx].subjects[selectedSubjectIdx].description}
//                     onChange={(e) =>
//                       handleSubjectChange(selectedLessonIdx, selectedSubjectIdx, "description", e.target.value)
//                     }
//                     variant="outlined"
//                     multiline
//                     rows={3}
//                     className="form-input"
//                   />
//                   <div className="form-group">
//                     <label>Thumbnail</label>
//                     <input
//                       type="file"
//                       accept=".jpg,.jpeg,.png"
//                       onChange={(e) => handleSubjectChange(selectedLessonIdx, selectedSubjectIdx, "thumbnail", e)}
//                       className="form-file"
//                     />
//                     {lessons[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail &&
//                       typeof lessons[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail === "string" && (
//                         <p className="current-file">
//                           Current: {lessons[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail.split("/").pop()}
//                         </p>
//                       )}
//                   </div>
//                 </div>
//               )}

//               {currentStep === 3 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
//                 <div className="form-section content-section">
//                   <h3>Add Content for Subject {selectedSubjectIdx + 1}</h3>
//                   <div className="content-types">
//                     {[
//                       { type: "pdf", label: "PDF", icon: faFilePdf },
//                       { type: "video", label: "Video", icon: faFilm },
//                       { type: "image", label: "Image", icon: faImage },
//                       { type: "test", label: "Test", icon: faPen },
//                     ].map(({ type, label, icon }) => (
//                       <div
//                         key={type}
//                         className="content-type-card"
//                         onClick={() => addContent(type)}
//                         style={{
//                           display: "flex",
//                           alignItems: "center",
//                           gap: "8px",
//                           cursor: "pointer",
//                         }}
//                       >
//                         <FontAwesomeIcon icon={icon} className="content-icon" />
//                         <span>Add {label}</span>
//                       </div>
//                     ))}
//                   </div>
//                   {showVideoPrompt && (
//                     <div className="video-input-options">
//                       <label className="video-option-label">Select Video Source</label>
//                       <div className="radio-group">
//                         <label>
//                           <input
//                             type="radio"
//                             checked={!isYouTube}
//                             onChange={() => {
//                               setIsYouTube(false);
//                               setYouTubeUrl("");
//                               setYouTubeError("");
//                             }}
//                           />
//                           Upload Video File
//                         </label>
//                         <label>
//                           <input
//                             type="radio"
//                             checked={isYouTube}
//                             onChange={() => setIsYouTube(true)}
//                           />
//                           YouTube Link
//                         </label>
//                       </div>
//                       {isYouTube ? (
//                         <div className="youtube-input">
//                           <TextField
//                             fullWidth
//                             label="YouTube URL"
//                             value={youTubeUrl}
//                             onChange={(e) => {
//                               setYouTubeUrl(e.target.value);
//                               setYouTubeError("");
//                             }}
//                             placeholder="https://www.youtube.com/watch?v=..."
//                             variant="outlined"
//                             className="form-input"
//                           />
//                           {youTubeError && <p className="error-text">{youTubeError}</p>}
//                           <div className="video-prompt-actions">
//                             <Button variant="primary" onClick={addVideoContent} className="youtube-btn">
//                               Add YouTube Video
//                             </Button>
//                             <Button variant="outline-secondary" onClick={() => setShowVideoPrompt(false)}>
//                               Cancel
//                             </Button>
//                           </div>
//                         </div>
//                       ) : (
//                         <div className="video-upload-section">
//                           <input
//                             type="file"
//                             ref={fileInputRef}
//                             accept=".mp4,.mov"
//                             multiple
//                             onChange={handleFileInputChange}
//                             style={{ display: "none" }}
//                           />
//                           <div className="video-prompt-actions">
//                             <Button
//                               variant="primary"
//                               onClick={addVideoContent}
//                             >
//                               Select Video Files
//                             </Button>
//                             <Button
//                               variant="outline-secondary"
//                               onClick={() => setShowVideoPrompt(false)}
//                             >
//                               Cancel
//                             </Button>
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                   <div className="content-list">
//                     {["pdf", "video", "image", "test"].map((type) => {
//                       const files = lessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type];
//                       return files.length > 0 ? (
//                         <div key={type} className="content-group">
//                           <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
//                           <div className="content-files">
//                             {files.map((file, i) => (
//                               <div key={i} className="content-file">
//                                 {type === "video" && file.type === "youtube" ? (
//                                   <iframe
//                                     width="100"
//                                     height="60"
//                                     src={`https://www.youtube.com/embed/${getYouTubeVideoId(file.url)}`}
//                                     title={file.name}
//                                     className="content-preview"
//                                   />
//                                 ) : type === "image" ? (
//                                   <img src={file.url} alt={file.name} className="content-preview" />
//                                 ) : (
//                                   <FontAwesomeIcon
//                                     icon={type === "pdf" ? faFilePdf : type === "video" ? faFilm : type === "image" ? faImage : faPen}
//                                     className="content-icon"
//                                   />
//                                 )}
//                                 <span>{file.name}</span>
//                                 <IconButton onClick={() => deleteContent(type, i)} aria-label={`Delete ${type} content`}>
//                                   <MdDelete size={16} color="#dc3545" />
//                                 </IconButton>
//                               </div>
//                             ))}
//                           </div>
//                         </div>
//                       ) : null;
//                     })}
//                     {["pdf", "video", "image", "test"].every(
//                       (type) => lessons[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].length === 0
//                     ) && <p className="no-data">No content added yet.</p>}
//                   </div>
//                 </div>
//               )}

//               <div className="form-actions">
//                 {currentStep > 0 && (
//                   <Button
//                     variant="outline-secondary"
//                     onClick={() => {
//                       if (currentStep === 1) setCurrentStep(0);
//                       else if (currentStep === 2) setCurrentStep(1);
//                       else if (currentStep === 3) setCurrentStep(2);
//                     }}
//                   >
//                     Back
//                   </Button>
//                 )}
//                 <div className="action-buttons">
//                   {currentStep === 0 && (
//                     <Button variant="outline-primary" onClick={() => setCurrentStep(1)}>
//                       Edit Lessons
//                     </Button>
//                   )}
//                   {currentStep === 1 && (
//                     <Button variant="outline-primary" onClick={() => addSubject(selectedLessonIdx)}>
//                       + Create New Subject
//                     </Button>
//                   )}
//                   {currentStep === 2 && (
//                     <Button variant="outline-primary" onClick={() => setCurrentStep(3)}>
//                       Add Content
//                     </Button>
//                   )}
                
//                     <Button variant="primary" type="submit" disabled={loader}>
//                       Update Course
//                     </Button>
                  
//                 </div>
//               </div>
//             </form>
//           </div>
//         </div>
//       </>
    
//     }

       
//     </Dialog>
//   );
// };

// export default CreateCourseDialog;

